import React from 'react';
import { Tag, Tooltip } from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import css from './DraftLanguageChip.module.scss';

interface DraftLanguageChipProps {
  language?: string;
  languageName?: string;
  reason?: string;
}

/**
 * "Drafted in {languageName}" chip with the backend's `reason` sentence as a
 * tooltip. Deliberately renders NOTHING for English (the quiet default) or when
 * no language metadata is present — so it stays invisible while the backend
 * localization flag is off, and "lights up" automatically when it flips.
 *
 * `languageName` and `reason` are shown verbatim from the backend; we never map
 * a code to a name ourselves (the backend may return languages outside our list).
 */
const DraftLanguageChip: React.FC<DraftLanguageChipProps> = ({
  language,
  languageName,
  reason,
}) => {
  if (!language || language === 'en' || !languageName) return null;

  const chip = (
    <Tag className={css.chip} icon={<TranslationOutlined />}>
      Drafted in {languageName}
    </Tag>
  );

  // `reason` can legitimately be an empty string — only wrap in a tooltip when
  // there is something to show. Make it keyboard/focus reachable, not hover-only.
  return reason ? (
    <Tooltip title={reason} trigger={['hover', 'focus']}>
      <span className={css.chipWrap} tabIndex={0}>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
};

export default DraftLanguageChip;
